/**
 * Splits a raw phone string into { countryCode, number }.
 *
 * Rules:
 *   - digits.length > 13  → invalid, returns empty strings
 *   - digits.length > 10  → countryCode = leading (len-10) digits, number = last 10
 *   - digits.length === 10 → countryCode defaults to "1" (US), number = all digits
 *   - digits.length < 10  → countryCode defaults to "1", number = digits (best effort)
 */
export function splitPhone(raw: string): { countryCode: string; number: string } {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) return { countryCode: "1", number: "" };
  if (digits.length > 13) return { countryCode: "", number: "" };
  if (digits.length > 10) {
    return {
      countryCode: digits.slice(0, digits.length - 10),
      number: digits.slice(digits.length - 10),
    };
  }
  return { countryCode: "1", number: digits };
}

/**
 * Formats a raw digit string progressively as (XXX) XXX-XXXX.
 * Strips non-digits and caps at 10.
 */
export function formatPhoneNumber(raw: string): string {
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Combines a country code and phone number into +XXXXXXXXXXX for API submission.
 * Returns empty string if either part is missing.
 */
export function combinePhone(countryCode: string, number: string): string {
  const cc = countryCode.replace(/\D/g, "");
  const num = number.replace(/\D/g, "");
  if (!cc || !num) return "";
  return `+${cc}${num}`;
}

/** A +1 (North American) number as "+1 555 010 0177"; any other number exactly as given. */
export function formatPhone(phone: string): string {
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(phone.replace(/[\s().-]/g, ""));
  return match ? `+1 ${match[1]} ${match[2]} ${match[3]}` : phone;
}
